'use client';

import React from 'react';
import { motion } from 'framer-motion';
import Script from 'next/script';
import SectionBase from '@/components/ui/sections/section-base';
import SectionHeader from '@/components/ui/sections/section-header';
import { TypographyH4, TypographySmall } from '@/components/ui/typography';
import { cls } from '@/lib/utils';
import type { ProjectFAQ } from '../../data/project-data';

const SECTION_ID = 'project-faq';

const wrapperMotionProps = {
  initial: 'hidden' as const,
  whileInView: 'visible' as const,
  viewport: { once: true },
  variants: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 },
    },
  },
};

const itemMotionProps = {
  variants: {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  },
};

interface ProjectFAQSectionProperties {
  faqs: ProjectFAQ[];
  projectName: string;
}

function ProjectFAQSection({
  faqs,
  projectName,
}: ProjectFAQSectionProperties) {
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <Script
        id={`faq-schema-${projectName.toLowerCase().replace(/\s+/g, '-')}`}
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <SectionBase id={SECTION_ID} background="bg-background">
        <motion.div
          className={cls`flex flex-col items-center gap-10`}
          {...wrapperMotionProps}
        >
          <SectionHeader
            subtitle="FAQ"
            title={`Frequently Asked Questions`}
            description={`Common questions about the ${projectName} project and custom home building in Claremore, Oklahoma.`}
          />

          <div className={cls`flex flex-col gap-6 w-full max-w-3xl mx-auto`}>
            {faqs.map((faq) => (
              <motion.div
                key={faq.question}
                className={cls`flex flex-col gap-3 p-6 rounded-lg border border-border bg-card`}
                {...itemMotionProps}
              >
                <TypographyH4 className="text-foreground">
                  {faq.question}
                </TypographyH4>
                <TypographySmall className="text-muted-foreground leading-relaxed">
                  {faq.answer}
                </TypographySmall>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </SectionBase>
    </>
  );
}

export default ProjectFAQSection;
